import classNames from 'classnames';
import { memo, useCallback, useEffect, useState } from 'react';

import { InlineInput, type InlineInputProps } from '@air/component-inline-input';
import { type ButtonProps } from '@air/primitive-button';
import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { EditableMultilineTextTriggerButton } from '~/_components/InlineInput/EditableMultilineTextTriggerButton';

export const resizableEditableTextInputButton = tailwindVariants({
  base: 'h-full w-full text-[length:inherit] leading-[inherit]',
  variants: {
    size: {
      small: 'py-1.5',
      medium: 'py-2.5',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

export const resizableEditableInput = tailwindVariants({
  base: 'h-full ads:text-secondary text-[length:inherit] text-gray-11 leading-[inherit] md:text-[length:inherit] md:leading-[inherit]',
  variants: {
    size: {
      small: 'py-1',
      medium: 'py-2',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

type ResizableEditableTextInputButtonVariants = VariantProps<typeof resizableEditableTextInputButton>;

export type ResizableEditableTextInputProps = Pick<
  InlineInputProps,
  'onSubmit' | 'defaultValue' | 'onChange' | 'label' | 'validate' | 'maxLength' | 'allowNewLines' | 'inputClassName'
> &
  ResizableEditableTextInputButtonVariants &
  Pick<ButtonProps, 'appearance'> & {
    'data-testid'?: string;
    disabled?: boolean;
    placeholder: string;
    className?: string;
    triggerClassName?: string;
  };

export const ResizableEditableTextInput = memo(
  ({
    disabled = false,
    onSubmit,
    defaultValue,
    appearance,
    onChange,
    className,
    'data-testid': testId,
    placeholder,
    size = 'medium',
    label,
    validate,
    maxLength,
    allowNewLines,
    inputClassName,
    triggerClassName,
  }: ResizableEditableTextInputProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
      setValue(defaultValue);
    }, [defaultValue]);

    const handleStartEditing = useCallback(() => {
      setIsEditing(true);
    }, []);

    const handleSubmitValue = useCallback(
      (nextValue: string) => {
        setIsEditing(false);
        onSubmit?.(nextValue);
      },
      [onSubmit],
    );

    const handleCancelEditing = useCallback(() => {
      setValue(defaultValue);
      setIsEditing(false);
    }, [defaultValue]);

    const handleChangeValue = useCallback(
      (nextValue: string) => {
        setValue(nextValue);
        onChange?.(nextValue);
      },
      [onChange],
    );

    return (
      <div
        className={cn('relative w-full text-16 md:text-14', isEditing ? 'min-w-[130px]' : '', className)}
        data-testid={testId}
      >
        <EditableMultilineTextTriggerButton
          disabled={disabled}
          placeholder={placeholder}
          appearance={appearance}
          onClick={handleStartEditing}
          style={{
            fontWeight: 'inherit',
          }}
          className={resizableEditableTextInputButton({
            size,
            className: cn(isEditing ? 'invisible' : 'visible', triggerClassName),
          })}
          value={value}
        />
        {isEditing && (
          <div className={classNames('absolute inset-0', appearance === 'outline' ? 'pt-0.5' : '-mx-px pt-px')}>
            <InlineInput
              allowNewLines={allowNewLines}
              label={label}
              onSubmit={handleSubmitValue}
              onCancel={handleCancelEditing}
              className="h-full"
              onChange={handleChangeValue}
              style={{
                fontWeight: 'inherit',
                overflow: 'hidden',
              }}
              inputClassName={resizableEditableInput({ size, className: inputClassName })}
              defaultValue={value}
              placeholder={placeholder}
              validate={validate}
              maxLength={maxLength}
            />
          </div>
        )}
      </div>
    );
  },
);

ResizableEditableTextInput.displayName = 'ResizableEditableTextInput';
